// frontend/modules/customer-category/hooks/useCustomerCategories.ts
import { useCallback, useEffect, useRef } from 'react';
import { useCustomerCategoryStore } from '../stores/customer-category-store';
import { message } from 'antd';
import {
    getAllCategories,
    getCategoryById,
    createCategory as createCategoryApi,
    updateCategory as updateCategoryApi,
    deleteCategory as deleteCategoryApi,
    updateCategoryStatus as updateCategoryStatusApi,
    useGetAllCategories
} from '@/shared/api/client/customer-category-controller';
import {CreateCustomerCategoryDTO, UpdateCustomerCategoryDTO} from "@/shared/api/model";

export const useCustomerCategories = () => {
    const {
        categories,
        activeCategory,
        isLoading,
        error,
        formMode,

        setCategories,
        setActiveCategory,
        setIsLoading,
        setError,
        setFormMode,
        resetState
    } = useCustomerCategoryStore();

    // Track the currently loading category ID to prevent duplicate requests
    const loadingCategoryIdRef = useRef<number | null>(null);

    // Fetch all categories
    const fetchCategories = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await getAllCategories();
            setCategories(response);
            setError(null); // Clear any previous errors
        } catch (error: any) {
            console.error('Error fetching categories:', error);
            setError(error.message || 'Failed to fetch categories');
            message.error('Failed to fetch customer categories');
        } finally {
            setIsLoading(false);
        }
    }, []); // Empty dependency array since we're using the latest state via setters

    // Fetch a single category by ID
    const fetchCategoryById = useCallback(async (id: number) => {
        // Prevent duplicate requests for the same category
        if (loadingCategoryIdRef.current === id) {
            return;
        }

        // Get current state values directly from store to avoid stale closures
        const currentState = useCustomerCategoryStore.getState();
        const currentActiveCategory = currentState.activeCategory;
        const currentIsLoading = currentState.isLoading;

        // If we already have the category loaded, don't fetch again
        if (currentActiveCategory?.id === id && !currentIsLoading) {
            return;
        }

        try {
            loadingCategoryIdRef.current = id;
            setIsLoading(true);

            const response = await getCategoryById(id);
            setActiveCategory(response);
            setError(null); // Clear any previous errors

        } catch (error: any) {
            console.error('Error fetching category:', error);
            setError(error.message || 'Failed to fetch category');
            message.error('Failed to fetch customer category');
        } finally {
            loadingCategoryIdRef.current = null;
            setIsLoading(false);
        }
    }, [setIsLoading, setActiveCategory, setError]); // Include stable setters as dependencies

    // Create a new category
    const createCategory = useCallback(async (data: CreateCustomerCategoryDTO) => {
        try {
            setIsLoading(true);
            const response = await createCategoryApi(data);
            // Refresh the list
            fetchCategories();
            return response;
        } catch (error: any) {
            console.error('Error creating category:', error);
            setError(error.message || 'Failed to create category');
            message.error('Failed to create customer category');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [fetchCategories]);

    // Update an existing category
    const updateCategory = useCallback(async (data: UpdateCustomerCategoryDTO) => {
        if (!activeCategory?.id) {
            message.error('No active category to update');
            return;
        }

        try {
            setIsLoading(true);
            const response = await updateCategoryApi(activeCategory.id, data);
            // Refresh the list
            fetchCategories();
            return response;
        } catch (error: any) {
            console.error('Error updating category:', error);
            setError(error.message || 'Failed to update category');
            message.error('Failed to update customer category');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [activeCategory, fetchCategories]);

    // Delete a category
    const deleteCategory = useCallback(async (id: number) => {
        try {
            setIsLoading(true);
            await deleteCategoryApi(id);
            // Refresh the list
            fetchCategories();
            message.success('Customer category deleted successfully');
        } catch (error: any) {
            console.error('Error deleting category:', error);
            setError(error.message || 'Failed to delete category');
            message.error('Failed to delete customer category');
        } finally {
            setIsLoading(false);
        }
    }, [fetchCategories]);

    // Update category status
    const updateCategoryStatus = useCallback(async (id: number, status: string) => {
        try {
            setIsLoading(true);
            await updateCategoryStatusApi(id, status);
            // Refresh the list
            fetchCategories();
            message.success(`Customer category ${status === 'ACTIVE' ? 'activated' : 'deactivated'} successfully`);
        } catch (error: any) {
            console.error('Error updating category status:', error);
            setError(error.message || 'Failed to update category status');
            message.error('Failed to update customer category status');
        } finally {
            setIsLoading(false);
        }
    }, [fetchCategories]);

    return {
        // State
        categories,
        activeCategory,
        isLoading,
        error,
        formMode,

        // Actions
        fetchCategories,
        fetchCategoryById,
        createCategory,
        updateCategory,
        deleteCategory,
        updateCategoryStatus,
        setFormMode,
        resetState
    };
};